import { createColumnHelper } from '@tanstack/react-table';

import Table from '@/components/table/common/Table';
import { IPaginationTable } from '@/components/table/common/interfaces/IPaginationTable';
import Button from '@/components/ui/button';
import { HaggleInvite } from '@/interfaces/api/invite/IInvite';
import { haggleApiService } from '@/services/haggle.service';
import { notificationService } from '@/services/notification.service';

const columnHelper = createColumnHelper<HaggleInvite>();

const HaggleInvitesTable = ({
	invites,
	pagination,
	refetch,
}: {
	invites: HaggleInvite[];
	pagination: IPaginationTable;
	refetch: () => void;
}) => {
	const handleAction = async (
		inviteId: number,
		haggleEventId: number,
		status: 'approved' | 'rejected',
	) => {
		try {
			await haggleApiService.updateHaggleInviteStatus(inviteId, {
				status,
				haggleEventId,
			});
			notificationService.success(`Invite ${status}`);
			refetch();
		} catch (err) {
			notificationService.error('Failed to update status.');
		}
	};

	const columns = [
		columnHelper.accessor('id', {
			header: () => <div className="w-[100px]">ID</div>,
			cell: (info) => <div className="w-[100px]">{info.getValue()}</div>,
		}),
		columnHelper.accessor('invitedEmail', {
			header: () => <div className="w-[300px] text-center">Invited Email</div>,
			cell: (info) => (
				<div className="w-[300px] text-center">{info.getValue()}</div>
			),
		}),
		columnHelper.accessor('status', {
			header: () => <div className="w-[250px] text-center">Status</div>,
			cell: ({ row }) => {
				const { status, id, haggleEventId } = row.original;

				const statusColorMap: Record<string, string> = {
					approved: 'text-purpleBrand-500 bg-purpleBrand-100',
					rejected: 'text-red-600 bg-red-100',
					pending: 'text-yellow-700 bg-yellow-100',
				};

				if (status === 'pending') {
					return (
						<div className="w-[250px] justify-center flex gap-2">
							<Button
								onClick={() => handleAction(id, haggleEventId, 'approved')}
							>
								Accept
							</Button>
							<Button
								onClick={() => handleAction(id, haggleEventId, 'rejected')}
							>
								Reject
							</Button>
						</div>
					);
				}

				return (
					<div className="w-[250px] justify-center flex gap-2">
						<span
							className={`text-center px-3 py-1 rounded-full text-sm font-medium ${statusColorMap[status]}`}
						>
							{status}
						</span>
					</div>
				);
			},
		}),
		columnHelper.accessor('invitedDate', {
			header: () => <div className="w-[200px] text-center">Invited At</div>,
			cell: (info) => (
				<div className="w-[200px] text-center">
					{new Date(info.getValue()).toLocaleString()}
				</div>
			),
		}),
	];

	return (
		<div className="flex flex-col items-center w-full bg-white p-4 rounded-md shadow-md">
			{invites && (
				<Table<HaggleInvite>
					collection={{ data: invites, ...pagination }}
					columns={columns}
					paginationSection={pagination}
					dataTest="invites-table"
				/>
			)}
		</div>
	);
};

export default HaggleInvitesTable;
